/*
 * Copyright (c) 2024 Pegasystems Inc.
 * All rights reserved.
 */

import { ofType } from 'redux-observable';
import { of, empty, Observable } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';

import { ActionManagerInstance } from '../../actions/manager';
import constants, { publicConstants } from '../../constants';
import { getResolvedTargetName } from '../../router/routing-manager';
import { ACTION_GETOBJECT, ACTIVATE_CONTAINER_ITEM, DELETE_CASE_SUMMARY } from '../../store/state/actions';
import { removeContainerItem, mergeData, postProcess } from '../../store/state/actions/action-creators';
import { getDataObject, dispatch } from '../../store/state/state-manager';
import {
  addPreviewResetAction,
  getActionBasedOnContainerMode,
  getActionPayload,
  getActivationDetails,
  getContext,
  getDirtyDetails,
  isAddContainerAction,
  processDataToDispatchErrorAction,
  processViewResponse,
  addActionsToParentContext
} from '../../store/state/epics/util';
import { getTranslatedDXAPIResponse } from '../../utils/dxapi-response-translator';
import { updateKeyAndFlowName, isParentAndChildContextHoldingSameItem } from '../common-helper';
import {
  getContainerName,
  promiseHandler,
  getContainerNameFromContainerItemID,
  isActivateContainerItemActionType,
  isErrorActionType
} from '../../case/common-helper';
import { publishCaseOpenedEvent } from '../../case/common-events';
import { getTargetFromContainerItemID } from '../../container/container-utils';
import type { Action } from '../../store/state/state-manager';
import type { ActionsOut } from '../../case/types';

import { executeGetObjectRequest } from './async-request';
import { getOpenObjectByIdSemanticURL, handleWorkItemActivation } from './helper';
import { GET_OBJECT } from './action-creator';

const { RESOURCE_TYPES } = publicConstants;

const addActionsForGetObject = async (chainedData: any, actionsOut: Action[]) => {
  const { actionInPayload, activateContainerActionPayload, type, response, removeActionPayload, headers } = chainedData;
  const { context: actionInContext, skipBrowserSemanticUrlUpdate, debugInfo } = actionInPayload;
  const containerName = getContainerName(ACTION_GETOBJECT, actionInPayload);
  const target = getResolvedTargetName(containerName, actionInContext);

  // usecase: work item details are already available in redux store
  if (isActivateContainerItemActionType(type)) {
    await handleWorkItemActivation(actionsOut, {
      activateContainerActionPayload,
      actionInPayload
    });
  } else {
    // since the work item is not available, get the context
    const context = getContext(target, containerName, actionInContext, { buildNew: true });
    const translatedDXAPIResponse = getTranslatedDXAPIResponse(response);
    const semanticURL = getOpenObjectByIdSemanticURL(translatedDXAPIResponse);

    translatedDXAPIResponse.semanticURL = semanticURL;
    translatedDXAPIResponse.directUrlUpdation = !skipBrowserSemanticUrlUpdate;

    /* [TODO]: we shouldn't use "acName" anywhere, setting this value..since app-router uses this logic */
    const options = { acName: target, context };

    await processViewResponse(translatedDXAPIResponse, actionInPayload, options);

    const payload = getActionPayload(translatedDXAPIResponse, {
      target,
      context,
      containerItemID: context,
      semanticURL,
      headers,
      debugInfo
    });

    payload.caseViewMode = constants.REVIEW_MODE;
    payload.resourceType = RESOURCE_TYPES.OBJECT;

    updateKeyAndFlowName(payload, translatedDXAPIResponse);

    const action = getActionBasedOnContainerMode(target, payload);
    /* istanbul ignore next */
    if (removeActionPayload.containerItemID && isAddContainerAction(action)) {
      actionsOut.push(
        postProcess(removeContainerItem, {
          containerItemID: removeActionPayload.containerItemID,
          target
        })
      );
    }
    actionsOut.push(action);
  }
  addPreviewResetAction(actionsOut);
  const caseKey = actionInPayload.insKey;
  publishCaseOpenedEvent({ caseKey, actionInContext });
};

const getObjectById = (action$: Observable<Action>) => {
  let actionMgrID = 0;
  let actionsOut: ActionsOut[] = [];

  return action$.pipe(
    ofType(GET_OBJECT),
    map((action: Action) => {
      ({ actionMgrID } = action.payload);
      return action.payload;
    }),
    mergeMap((actionInPayload) => {
      const { context, isChild, viewType } = actionInPayload;

      const containerName = getContainerName(ACTION_GETOBJECT, actionInPayload);
      const target = getResolvedTargetName(containerName, context);

      let continueExecution = true;
      let removeActionPayload;
      if (viewType !== 'none') {
        const dirtyDetails = getDirtyDetails(target);

        continueExecution = dirtyDetails.continueExecution;
        removeActionPayload = dirtyDetails.removeActionPayload;
      }

      /* If user clicks on cancel on dirty dialog popup, return empty observable to stop the chaining process
         else continue the chaining process. */
      if (!continueExecution) {
        ActionManagerInstance.rejectActionByID(actionMgrID);
        return empty();
      }

      /* Check whether work item is already available in redux store.
         If already present activate the work item by disptaching ACTIVATE_CONTAINER_ITEM action */
      const { activate, activateContainerActionPayload } = getActivationDetails(
        RESOURCE_TYPES.OBJECT,
        target,
        actionInPayload
      );

      if (activate && !isChild && viewType !== 'none') {
        return of({
          type: ACTIVATE_CONTAINER_ITEM,
          activateContainerActionPayload,
          actionInPayload
        });
      }

      return executeGetObjectRequest(actionInPayload, { removeActionPayload });
    }),
    mergeMap(async (chainedData) => {
      if (chainedData && isErrorActionType(chainedData.type)) {
        actionsOut.push(processDataToDispatchErrorAction(chainedData));
        return actionsOut;
      }

      if (chainedData?.actionInPayload?.viewType === 'none') {
        const containerData = getDataObject(chainedData?.actionInPayload?.context);

        if (containerData?.context_data?.caseViewMode !== constants.PERFORM_MODE) {
          const translatedDXAPIResponse1 = getTranslatedDXAPIResponse(chainedData?.response);
          const currentPayload = {
            data: {
              ...translatedDXAPIResponse1?.data
            },
            context: chainedData?.actionInPayload?.context,
            isArrayDeepMerge: true
          };
          actionsOut.push({
            type: DELETE_CASE_SUMMARY,
            payload: {
              context: currentPayload.context,
              doDeleteConfirmationDetails: false
            }
          });
          actionsOut.push(postProcess(mergeData, currentPayload));
          const target = getTargetFromContainerItemID(chainedData?.actionInPayload?.context);
          const containerName = getContainerNameFromContainerItemID(chainedData?.actionInPayload?.context);

          const currentContainerInfo = Object.freeze({
            target,
            containerItemID: chainedData?.actionInPayload?.context,
            context: chainedData?.actionInPayload?.context,
            containerName
          });

          if (isParentAndChildContextHoldingSameItem(chainedData?.actionInPayload?.context)) {
            const payload = getActionPayload(chainedData?.response, {
              ...currentContainerInfo
            });
            addActionsToParentContext({ ...payload, isArrayDeepMerge: true }, actionsOut);
          }
        }
      } else {
        await addActionsForGetObject(chainedData, actionsOut);
      }
      return actionsOut;
    }),
    map(() => {
      dispatch(actionsOut);
      promiseHandler(actionsOut, actionMgrID);
      actionsOut = [];
      return actionsOut;
    })
  );
};

export default getObjectById;
